import { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { 
  Building2, 
  Users, 
  MessageSquare, 
  Send,
  TrendingUp,
  AlertCircle,
  DollarSign,
  Search,
  ChevronRight,
  RefreshCw,
  XCircle,
  Shield
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { getToken } = useTenant();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [failedCampaigns, setFailedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessDetail, setBusinessDetail] = useState(null);

  const fetchData = async () => {
    try {
      const token = await getToken();
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Fetch all data in parallel
      const [statsRes, businessesRes, failedRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/businesses?page_size=10`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/failed-campaigns?page_size=10`, { headers }),
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      } else if (statsRes.status === 403) {
        setError('Access denied. Super admin privileges required.');
        setLoading(false);
        return;
      }

      if (businessesRes.ok) {
        const data = await businessesRes.json();
        setBusinesses(data.businesses || []);
      }

      if (failedRes.ok) {
        const data = await failedRes.json();
        setFailedCampaigns(data.campaigns || []);
      }
    } catch (err) {
      console.error('Admin data fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessDetail = async (businessId) => {
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/businesses/${businessId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        setBusinessDetail(await response.json());
        setSelectedBusiness(businessId);
      }
    } catch (err) {
      console.error('Business detail fetch error:', err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [getToken]);

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-28 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center">
          <Shield className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 mb-2">Access Denied</h3>
          <p className="text-sm text-red-600">{error}</p>
          <p className="text-sm text-red-500 mt-2">This page is restricted to super administrators only.</p>
        </div>
      </div>
    );
  }

  const statCards = [
    { name: 'Total Businesses', value: stats?.total_businesses || 0, icon: Building2, color: 'blue' },
    { name: 'Active Subscriptions', value: stats?.active_subscriptions || 0, icon: DollarSign, color: 'green' },
    { name: 'Total Users', value: stats?.total_users || 0, icon: Users, color: 'purple' },
    { name: 'SMS This Month', value: stats?.total_sms_this_month?.toLocaleString() || 0, icon: MessageSquare, color: 'indigo' },
  ];

  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'trial': return 'bg-blue-100 text-blue-700';
      case 'past_due': return 'bg-yellow-100 text-yellow-700';
      case 'canceled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
            <Shield className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Super Admin Dashboard</h1>
            <p className="text-gray-500 text-sm">Internal observability panel</p>
          </div>
        </div>
        <Button
          onClick={fetchData}
          variant="outline"
          className="flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`w-10 h-10 rounded-lg ${colorClasses[stat.color]} flex items-center justify-center`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Additional Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500">Trial Subscriptions</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{stats?.trial_subscriptions || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500">Active Businesses</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{stats?.active_businesses || 0}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <p className="text-sm text-gray-500">Total SMS Sent (All Time)</p>
          <p className="text-xl font-bold text-gray-900 mt-1">{stats?.total_sms_sent?.toLocaleString() || 0}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Businesses List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Recent Businesses</h2>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-48 h-8 text-sm"
              />
            </div>
          </div>

          <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
            {businesses
              .filter(b => b.name.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((business) => (
                <div 
                  key={business.id}
                  className="p-4 hover:bg-gray-50 cursor-pointer"
                  onClick={() => fetchBusinessDetail(business.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{business.name}</p>
                      <p className="text-xs text-gray-500">{business.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {business.subscription_status && (
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(business.subscription_status)}`}>
                          {business.subscription_status}
                        </span>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{business.sms_used.toLocaleString()} / {business.sms_limit.toLocaleString()} SMS</span>
                    <span>{business.customer_count} customers</span>
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* Failed Campaigns */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Failed Campaigns (Last 30 Days)
            </h2>
          </div>

          {failedCampaigns.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
              No failed campaigns
            </div>
          ) : (
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {failedCampaigns.map((campaign) => (
                <div key={campaign.id} className="p-4">
                  <div className="flex items-center justify-between mb-1">
                    <p className="font-medium text-gray-900">{campaign.campaign_name}</p>
                    <span className="text-red-600 font-medium text-sm">{campaign.failure_rate}% failed</span>
                  </div>
                  <p className="text-xs text-gray-500">{campaign.business_name}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>{campaign.failed_count} / {campaign.total_recipients} failed</span>
                    <span>{new Date(campaign.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Business Detail Modal */}
      {businessDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setBusinessDetail(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{businessDetail.business?.name}</h2>
                  <p className="text-sm text-gray-500">{businessDetail.business?.email}</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setBusinessDetail(null)}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Business Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Slug</p>
                  <p className="text-sm font-medium">{businessDetail.business?.slug}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Phone</p>
                  <p className="text-sm font-medium">{businessDetail.business?.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Timezone</p>
                  <p className="text-sm font-medium">{businessDetail.business?.timezone || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">KYC Verified</p>
                  <p className="text-sm font-medium">{businessDetail.business?.kyc_verified ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Stripe Customer ID</p>
                  <p className="text-sm font-medium font-mono">{businessDetail.business?.stripe_customer_id || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Created</p>
                  <p className="text-sm font-medium">{new Date(businessDetail.business?.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Users */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Users ({businessDetail.users?.length || 0})</h3>
                <div className="bg-gray-50 rounded-lg divide-y divide-gray-200">
                  {businessDetail.users?.map(user => (
                    <div key={user.id} className="p-3 flex items-center justify-between">
                      <span className="text-sm">{user.email}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${user.role === 'owner' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}>
                        {user.role}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Subscriptions */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Subscription</h3>
                {businessDetail.subscriptions?.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg p-3">
                    {businessDetail.subscriptions.map(sub => (
                      <div key={sub.id} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(sub.status)}`}>
                            {sub.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {sub.sms_used} / {sub.sms_limit} SMS
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 font-mono">{sub.stripe_subscription_id}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No subscription</p>
                )}
              </div>

              {/* SMS Stats */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">SMS Stats</h3>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(businessDetail.sms_stats || {}).map(([status, count]) => (
                    <div key={status} className="bg-gray-50 rounded-lg p-2 text-center">
                      <p className="text-lg font-bold">{count}</p>
                      <p className="text-xs text-gray-500">{status}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
